import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Config from '../config/config';
import { getAuthPersonIdSelector } from '../redux/oauth/oauthSelectors';
import { getPersonByIdSelector } from '../redux/person/personSelector';

interface Props {
  children: React.ReactNode;
}

export const LaunchDarklyProvider: React.FC<Props> = ({ children }) => {
  const [Provider, setProvider] = useState<React.ComponentType<Props> | null>(null);
  const authPersonId = useSelector(getAuthPersonIdSelector);
  const personById = useSelector(getPersonByIdSelector);
  const authPerson = authPersonId ? personById[authPersonId] : null;
  let reactAppLaunchDarklyClientId = Config.getReactAppLaunchDarklyClientId();
  const developmentEnv = Config.getDeploymentEnv();
  if (developmentEnv === 'staging') {
    reactAppLaunchDarklyClientId = '67f55f2df4f907098f1b6fe2';
  } else if (developmentEnv === 'production') {
    reactAppLaunchDarklyClientId = '67f55f2df4f907098f1b6fe3';
  } else {
    reactAppLaunchDarklyClientId = '67fed8c46d176408e665c1be';
  }
  console.log('reactAppLaunchDarklyClientId: ', reactAppLaunchDarklyClientId);

  const [authPersonName, setAuthPersonName] = useState<string | null>(null);
  const [authPersonEmail, setAuthPersonEmail] = useState<string | null>(null);
  const [authPersonOrganizationId, setAuthPersonOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    if (!authPersonId || !personById || !authPerson) {
      return;
    }
    setAuthPersonName(authPerson.fullName ?? null);
    setAuthPersonEmail(authPerson.email ?? null);
    setAuthPersonOrganizationId(authPerson.organizationId ?? null);
  }, [authPersonId, personById]);

  useEffect(() => {
    const initLD = async () => {
      const LDProvider = (await asyncWithLDProvider({
        clientSideID: reactAppLaunchDarklyClientId,
        context: {
          kind: 'user',
          key: authPersonId || 'anonymous',
          name: authPersonName,
          email: authPersonEmail,
          userId: authPersonId,
          organizationId: authPersonOrganizationId
        },
        options: {
          bootstrap: 'localStorage'
        }
      })) as React.ComponentType<Props>;
      setProvider(() => LDProvider);
    };

    initLD();
  }, [authPersonId, authPersonName, authPersonEmail, authPersonOrganizationId, reactAppLaunchDarklyClientId]);

  if (!Provider) return null;

  return <Provider>{children}</Provider>;
};
